import type { PerpsProvider } from '@kwenta/sdk/types'
import { formatNumber } from '@kwenta/sdk/utils'
import { useRouter } from 'next/router'
import {
	type Dispatch,
	type SetStateAction,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import CaretDownIcon from 'assets/svg/app/caret-down.svg'
import TradingRewardsIcon from 'assets/svg/app/gift.svg'
import { BaseCard } from 'components/Card/style'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { PROVIDER_CHAIN_NAMES, PROVIDER_MAINNET_CHAINS } from 'constants/network'
import useProvidersStats from 'hooks/useProvidersStats'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { useAppSelector } from 'state/hooks'
import { linkCSS } from 'styles/common'

import type { NetworkItem } from '../constants'

import Connector from 'containers/Connector'
import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import { useNavDropdown } from './NavDropdown'

type FuturesDropDown = {
	i18nLabel: string
	networks: NetworkItem[]
}

const IntegrationsDropDown = memo(({ i18nLabel, networks }: FuturesDropDown) => {
	const { t } = useTranslation()
	const { pathname } = useRouter()
	const { isOpen, setIsOpen } = useNavDropdown()

	const isMarketsPage = useMemo(() => pathname.includes('/market'), [pathname])
	const { isolatedMarginProviders, crossMarginProviders } = useMemo(() => {
		return {
			isolatedMarginProviders: networks.filter((n) => n.category === 'isolated-margin'),
			crossMarginProviders: networks.filter((n) => n.category === 'cross-margin'),
		}
	}, [networks])

	const hasIsolated = useMemo(() => {
		return networks.some((n) => n.category === 'isolated-margin')
	}, [networks])

	const hasCrossMargin = useMemo(() => {
		return networks.some((n) => n.category === 'cross-margin')
	}, [networks])

	const openDropDown = useCallback(() => {
		setIsOpen(true)
	}, [setIsOpen])

	const closeDropDown = useCallback(() => {
		setIsOpen(false)
	}, [setIsOpen])

	const [focusedProvider, setFocusedProvider] = useState<PerpsProvider>()

	return (
		<div onMouseEnter={openDropDown} onMouseLeave={closeDropDown} key={i18nLabel}>
			<FlexDivRow style={{ cursor: 'pointer' }}>
				<MenuInside $isActive={isOpen || isMarketsPage}>{t(i18nLabel)}</MenuInside>
				<StyledCaretDownIcon $flip={isOpen} $isActive={isMarketsPage} />
			</FlexDivRow>
			<Spacer open={isOpen} />
			<NetworksDropdownContainer open={isOpen}>
				<NetworksDropdownContent>
					<ProviderSelectionContainer>
						{hasCrossMargin && (
							<CategoryProviders
								category={t('header.nav.dropdown.cross-margin')}
								providers={crossMarginProviders}
								focusedProvider={focusedProvider}
								setFocusedProvider={setFocusedProvider}
								showVolHeader
							/>
						)}
						{hasIsolated && (
							<CategoryProviders
								category={t('header.nav.dropdown.isolated-margin')}
								providers={isolatedMarginProviders}
								focusedProvider={focusedProvider}
								setFocusedProvider={setFocusedProvider}
								showVolHeader={!hasCrossMargin}
							/>
						)}
					</ProviderSelectionContainer>
				</NetworksDropdownContent>
			</NetworksDropdownContainer>
		</div>
	)
})

const CategoryProviders = memo(
	({
		category,
		providers,
		focusedProvider,
		setFocusedProvider,
		showVolHeader,
	}: {
		category: string
		providers: NetworkItem[]
		focusedProvider?: PerpsProvider
		setFocusedProvider: Dispatch<SetStateAction<PerpsProvider | undefined>>
		showVolHeader?: boolean
	}) => {
		const router = useRouter()
		const { switchToChain } = Connector.useContainer()
		const { isOpen, setIsOpen } = useNavDropdown()
		const { totalDailyVolume } = useProvidersStats()
		const provider = useAppSelector(selectPerpsProvider)
		const isActive = useCallback((name: PerpsProvider) => provider === name, [provider])
		const marketPageRoute = useMarketPageRoute()

		const handleProviderSwitch = useCallback(
			(provider: PerpsProvider) => {
				switchToChain(PROVIDER_MAINNET_CHAINS[provider])
				router.push(marketPageRoute(provider))
				setIsOpen(false)
			},
			[router, switchToChain, setIsOpen, marketPageRoute]
		)

		const handleOnProviderHover = useCallback(
			(provider?: PerpsProvider) => {
				setFocusedProvider(provider)
			},
			[setFocusedProvider]
		)

		useEffect(() => {
			if (!isOpen) {
				setFocusedProvider(undefined)
			}
		}, [isOpen, setFocusedProvider])

		return (
			<CategorySelectionContainer>
				<CategoryContainer>
					<Body weight="bold" size="large" capitalized>
						{category}
					</Body>
					{showVolHeader && (
						<Body weight="bold" size="large" capitalized>
							Vol
						</Body>
					)}
				</CategoryContainer>
				<ProvidersContainer>
					{providers.map((provider) => {
						return (
							<ProviderContainer
								key={provider.name}
								onMouseEnter={() => handleOnProviderHover(provider.provider)}
								onMouseLeave={() => handleOnProviderHover()}
								onClick={() => handleProviderSwitch(provider.provider)}
								$focused={focusedProvider === provider.provider}
							>
								<FlexDivRow>
									{provider.icon}
									<NetworkName
										size="large"
										color={isActive(provider.provider) ? 'preview' : 'primary'}
									>
										{PROVIDER_CHAIN_NAMES[provider.provider]}
									</NetworkName>
									{provider.hasRewards && <StyledTradingRewardsIcon />}
								</FlexDivRow>
								<Body size="medium" color="secondary">
									{formatNumber(totalDailyVolume[provider.provider], {
										truncateOver: 1e3,
										maxDecimals: 0,
									})}
								</Body>
							</ProviderContainer>
						)
					})}
				</ProvidersContainer>
			</CategorySelectionContainer>
		)
	}
)

const ProviderSelectionContainer = styled(FlexDivCol)`
	width: 270px;
	padding: 0 15px;
	gap: 10px;
`

const CategorySelectionContainer = styled(FlexDivCol)`
	gap: 10px;
`

const CategoryContainer = styled(FlexDivRow)`
	justify-content: space-between;
	padding: 0 10px;
`

const ProvidersContainer = styled(FlexDivCol)`
	gap: 10px;
`

const NetworkName = styled(Body)`
  text-transform: capitalize;

`
const ProviderContainer = styled(FlexDivRow)<{ $focused: boolean }>`
	padding: 0px 10px;
	justify-content: space-between;
	align-items: center;
	height: 35px;
	border-radius: 8px;
	cursor: pointer;

	background-color: ${(props) =>
		props.$focused
			? props.theme.colors.selectedTheme.newTheme.button.cell.hover.background
			: props.theme.colors.selectedTheme.newTheme.button.cell.background};

	& > div {
		align-items: center;
		justify-content: flex-start;
		gap: 5px;
	}
`

const MenuInside = styled.div<{ $isActive: boolean; $isDropDown?: boolean }>`
	${linkCSS};
	padding: ${(props) => (props.$isDropDown ? '4px 9px' : '0px')};
	font-weight: 400;
	font-size: 14px;
	text-transform: capitalize;
	text-align: center;
	border-radius: 100px;
	background: transparent;
	cursor: pointer;
	width: 100%;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	opacity: ${(props) => (props.$isActive ? 1 : 0.5)};
	&:hover {
		opacity: 1;
	}
	transition: all 0.2s ease-in-out;
`

const NetworksDropdownContainer = styled.div<{ open: boolean }>`
	z-index: 100;
	position: absolute;
	top: 50px;
	left: 250px;
	width: 540px;
	opacity: ${(props) => (props.open ? 1 : 0)};
	display: ${(props) => (props.open ? 'block' : 'none')};
	transition: opacity 0.2s ease-in-out;
`

const Spacer = styled(NetworksDropdownContainer)`
	top: 35px;
	width: 85px;
	height: 25px;
	background-color: transparent;
`

const NetworksDropdownContent = styled(BaseCard)`
	display: flex;
	flex-direction: row;
	width: 270px;
	padding: 15px 0px;
	border-radius: 8px;
	height: 165px;
`

const StyledCaretDownIcon = styled(CaretDownIcon)<{
	$flip?: boolean
	$isActive: boolean
}>`
	width: 12px;
	margin-left: 5px;
	${(props) => css`
		transform: ${props.$flip ? 'rotate(180deg)' : 'none'};
		color: ${props.theme.colors.selectedTheme.gray};
	`}
	opacity: ${(props) => (props.$flip || props.$isActive ? 1 : 0.5)};
	&:hover {
		opacity: 1;
	}
	transition: all 0.2s ease-in-out;
	path {
		stroke-width: 1.5;
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const StyledTradingRewardsIcon = styled(TradingRewardsIcon)<{ disabled: boolean }>`
	path {
		fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
	}
`

export default IntegrationsDropDown
